import { Box, Grid, Text } from '@factorialco/deprecated-design-system'
import { RichTextDisplay, RichTextEditor } from '@factorialco/f0-react/dist/experimental'
import { FormikProps } from 'formik'
import {
  TrainingRequestDetailFragment,
  TrainingRequestsEmployeeFragment,
} from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import TextArea from 'modules/core/components/TextArea'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { ApplicantBlock } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/ApplicantBlock'
import { ParticipantsBlock } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/ParticipantBlock'
import { TrainingsBlock } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/TrainingBlock'
import { RequestType, UpdateData } from 'modules/trainings/pages/MyTrainings/RequestDetail/types'

interface Props {
  trainingRequest: TrainingRequestDetailFragment
  form: FormikProps<UpdateData>
  selectedRequestType: string
  readonly: boolean
  employees: TrainingRequestsEmployeeFragment[]
  id?: string
  onChangeTrainingType: (value: RequestType) => void
}

export const FromCatalogComponent = ({
  trainingRequest,
  form,
  selectedRequestType,
  onChangeTrainingType,
  readonly,
  employees,
  id,
}: Props) => {
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()

  return (
    <Box paddingBottom="s12">
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 6 }}>
          <Box gap="s24">
            <Select
              label={i18n.t('trainings.training_requests.my_requests.detail.training_type.title')}
              options={[
                {
                  label: i18n.t('trainings.training_requests.create.button.new'),
                  value: 'newRequest',
                },
                {
                  label: i18n.t('trainings.training_requests.create.button.from_catalog'),
                  value: 'fromCatalog',
                },
              ]}
              readonly={readonly}
              value={selectedRequestType}
              onChange={(value) => onChangeTrainingType(value as RequestType)}
            />
            <Text size="200" weight="medium" color="grey1000">
              {i18n.t('trainings.training_requests.create.training_need_step')}
            </Text>
            {richTextEditorLoading ? (
              <RichTextEditor.Skeleton />
            ) : canUseRichTextEditor ? (
              readonly ? (
                <Box
                  border={{ all: { color: 'grey400', style: 'solid', width: 's2' } }}
                  borderRadius={{ all: 'abs008' }}
                  background="grey200"
                  paddingX="s16"
                  paddingY="s8"
                  minHeight="s64"
                >
                  <RichTextDisplay content={form.values.trainingNeed || ''} />
                </Box>
              ) : (
                <RichTextEditor
                  {...editorConfig}
                  title={i18n.t('trainings.training_requests.create.training_need_step')}
                  onChange={(data) => {
                    form.setValues({ ...form.values, trainingNeed: data?.value || '' })
                  }}
                  initialEditorState={{ content: form.values.trainingNeed || '' }}
                  placeholder={''}
                  maxCharacters={5000}
                />
              )
            ) : (
              <TextArea
                value={String(form.values.trainingNeed)}
                readonly={readonly}
                onChange={(value) => form.setValues({ ...form.values, trainingNeed: value })}
                mandatory
              />
            )}

            <TrainingsBlock readonly={readonly} form={form} />
            <ApplicantBlock trainingRequest={trainingRequest} />
            {employees && employees.length > 0 && (
              <ParticipantsBlock
                trainingRequest={trainingRequest}
                readonly={readonly}
                form={form}
                employees={employees}
              />
            )}
            <Text size="200" weight="medium" color="grey1000">
              {i18n.t('trainings.training_requests.my_requests.detail.additional_info')}
            </Text>
            {richTextEditorLoading ? (
              <RichTextEditor.Skeleton />
            ) : canUseRichTextEditor ? (
              readonly ? (
                <Box
                  border={{ all: { color: 'grey400', style: 'solid', width: 's2' } }}
                  borderRadius={{ all: 'abs008' }}
                  background="grey200"
                  paddingX="s16"
                  paddingY="s8"
                  minHeight="s64"
                >
                  <RichTextDisplay content={form.values.comments || ''} />
                </Box>
              ) : (
                <RichTextEditor
                  {...editorConfig}
                  title={i18n.t('trainings.training_requests.my_requests.detail.additional_info')}
                  onChange={(data) => {
                    form.setValues({ ...form.values, comments: data?.value || '' })
                  }}
                  initialEditorState={{ content: form.values.comments || '' }}
                  placeholder={''}
                  maxCharacters={5000}
                />
              )
            ) : (
              <TextArea
                value={String(form.values.comments)}
                readonly={readonly}
                onChange={(value) => form.setValues({ ...form.values, comments: value })}
              />
            )}
          </Box>
        </Box>
        <Box gridColSpan={{ xxs: 6 }}>
          <ApprovalsInformationBlock
            resourceId={String(id)}
            resourceType={'Trainings::TrainingRequest'}
          />
        </Box>
      </Grid>
    </Box>
  )
}
